import type { DbHealthResponse, HealthResponse } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";

type Tone = "ok" | "warn" | "error" | "loading";

const toneClass: Record<Tone, string> = {
  ok: "bg-success text-success-foreground",
  warn: "bg-warning text-warning-foreground",
  error: "bg-destructive text-destructive-foreground",
  loading: "bg-muted text-muted-foreground",
};

function StatusCard({
  title,
  label,
  tone,
  hint,
}: {
  title: string;
  label: string;
  tone: Tone;
  hint?: string;
}) {
  return (
    <section className="rounded-lg border bg-card p-5 text-card-foreground shadow-sm">
      <h2 className="text-sm font-medium text-muted-foreground">{title}</h2>
      <p className={`mt-3 inline-block rounded px-2 py-1 text-sm font-semibold ${toneClass[tone]}`}>
        {label}
      </p>
      {hint && <p className="mt-3 text-sm text-muted-foreground">{hint}</p>}
    </section>
  );
}

/** Situação técnica: confirma que frontend, API e banco estão ligados. */
export function StatusPage() {
  const api = useQuery({
    queryKey: ["health"],
    queryFn: () => apiFetch<HealthResponse>("/health", { withCompany: false, token: null }),
  });
  const db = useQuery({
    queryKey: ["health", "db"],
    queryFn: () => apiFetch<DbHealthResponse>("/health/db", { withCompany: false, token: null }),
    enabled: api.isSuccess,
  });

  const apiCard = api.isPending
    ? { label: "Verificando...", tone: "loading" as const }
    : api.isSuccess
      ? { label: `Online · v${api.data.version}`, tone: "ok" as const }
      : { label: "Sem resposta", tone: "error" as const, hint: "Inicie a API com: pnpm dev:api" };

  const dbCard = !api.isSuccess
    ? { label: "—", tone: "loading" as const }
    : db.isPending
      ? { label: "Verificando...", tone: "loading" as const }
      : db.data?.status === "ok"
        ? { label: `Conectado · ${db.data.latencyMs ?? 0} ms`, tone: "ok" as const }
        : db.data?.status === "unconfigured"
          ? { label: "Não configurado", tone: "warn" as const, hint: "Defina DATABASE_URL no .env" }
          : { label: "Falha na conexão", tone: "error" as const };

  return (
    <div>
      <h2 className="text-lg font-semibold">Situação do ambiente</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <StatusCard title="API" {...apiCard} />
        <StatusCard title="Banco de dados" {...dbCard} />
      </div>
    </div>
  );
}
